import type { ReactNode } from "react";
import { type CSSProperties, useEffect, useState } from "react";

interface DisappearProps {
  children?: ReactNode;
}

/**
 * Renders its children inside a `<div>` that animates out:
 *
 * 1. Immediately becomes fully visible with no transition.
 * 2. After 2s, fades out over 1s while its `max-height` is pinned to the
 *    viewport height (so the fade can happen without layout jumps).
 * 3. After 2.5s, collapses its `max-height` to `0` over 0.3s.
 */
function Disappear({ children }: DisappearProps): ReactNode {
  const [style, setStyle] = useState<CSSProperties>({});

  useEffect(() => {
    const handles = [
      setTimeout(
        () =>
          setStyle({
            transition: "0s all",
            opacity: 1,
            maxHeight: "none",
          }),
        1,
      ),
      setTimeout(
        () =>
          setStyle({
            transition: "1s opacity, 0s max-height",
            opacity: 0,
            maxHeight: window.innerHeight + "px",
          }),
        2000,
      ),
      setTimeout(
        () =>
          setStyle({
            transition: "1s opacity, 0.3s max-height",
            opacity: 0,
            maxHeight: 0,
            overflow: "hidden",
          }),
        2500,
      ),
    ];

    return () => handles.forEach(clearTimeout);
  }, []);

  return <div style={style}>{children}</div>;
}

/**
 * Wraps the given component/element in a short "disappear" animation.
 *
 * @param comp - The element/component to animate out.
 * @returns A react node rendering `comp` with a disappearing animation applied.
 */
export function disappear(comp: ReactNode): ReactNode {
  return <Disappear key={Math.random()}>{comp}</Disappear>;
}
